import {
	Document,
	Page,
	Text,
	View,
	StyleSheet,
	Image,
	Font,
} from "@react-pdf/renderer";
import {
	OrderModel,
	PaymentStatus,
	PaymentStatusOptions,
	TransactionTypeOptions,
} from "./OrderInterfaces";
import { defaultColors, lightThemeColors } from "../../assetsConfig";
import { TransactionType } from "../Checkout/OrderInterfaces";
import { getHierarchy } from "./manageCheckout";
import Placeholder from "../../assets/placeholders/placeholder.png";
import { getNonWebpThumbnailSrc } from "../../views/MediaLibrary/manageMediaLibrary";
import config from "../../config";
import { pdfColors } from "../../assetsConfig";
import assetsConfig from "../../assetsConfig";
import { getLocalTime } from "../../utils/timeUtilities";
import { copy } from "../../utils/copy";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { commafy, commafySymbol } from "../../utils/commafy";

// register primary font
Font.register({
	family: assetsConfig.fonts.pdf.primary.fontFamily,
	fonts: assetsConfig.fonts.pdf.primary.fontSrc,
});

// register default font
Font.register({
	family: assetsConfig.fonts.pdf.default.fontFamily,
	fonts: assetsConfig.fonts.pdf.default.fontSrc,
});

// use noto sans for currency to support Rupee symbol
Font.register({
	family: assetsConfig.fonts.pdf.currency.fontFamily,
	fonts: assetsConfig.fonts.pdf.currency.fontSrc,
});

// Create styles
const styles = StyleSheet.create({
	currencyText: {
		fontFamily: assetsConfig.fonts.pdf.currency.fontFamily,
		fontWeight: 400,
	},
	page: {
		display: "flex",
		flexDirection: "column",
		width: "100vw",
		height: "842pt", // A4 height
		flexGrow: 1,
		backgroundColor: "white",
		fontSize: "10pt",
		fontFamily:
			assetsConfig.fonts.pdf.activeFont === "primary"
				? assetsConfig.fonts.pdf.primary.fontFamily
				: assetsConfig.fonts.pdf.default.fontFamily,
		padding: "32pt",
		paddingBottom: "152pt", // padding bottom to avoid footer overlap
	},
	footer: {
		position: "absolute",
		left: 0,
		right: 0,
		bottom: "18pt",
		textAlign: "center",
		padding: "8pt 32pt",
		height: "108pt",
		fontSize: "9pt",
	},
	twoCol: {
		display: "flex",
		flexDirection: "row",
		// flexGrow: 1,
		justifyContent: "space-between",
		width: "100%",
	},
	flexCol: {
		display: "flex",
		flexDirection: "column",
	},
	flexRow: {
		display: "flex",
		flexDirection: "row",
	},
	itemsCenter: {
		alignItems: "center",
	},
	justifyCenter: {
		justifyContent: "center",
	},
	textNormal: {
		fontWeight: 400,
	},
	semiBold: {
		fontWeight: 600,
	},
	editInvoiceContainer: {
		border: `2px solid ${defaultColors.colorDanger}`,
		padding: "11px",
	},
	entryCard: {
		display: "flex",
		padding: "12px",
		border: `2px solid ${defaultColors.colorAwardGold}`,
	},
	entryFirstRow: {
		justifyContent: "space-between",
		marginBottom: "7.5px",
	},
	logo: {
		// width: "200px",
		height: "35px",
	},
	mtAuto: {
		marginTop: "auto",
		// justifyContent: "flex-end",
	},
	mlAuto: {
		marginLeft: "auto",
	},
	wFull: {
		width: "100%",
	},
	mr1: {
		marginRight: "16px",
	},
	mr3: {
		marginRight: "48px",
	},
	mr5: {
		marginRight: "90px",
	},
	mb1: {
		marginBottom: "16px",
	},
	mb3: {
		marginBottom: "48px",
	},
	mb4: {
		marginBottom: "64px",
	},
	mbHalf: {
		marginBottom: "8px",
	},
	mtHalf: {
		marginTop: "8px",
	},
	mt1: {
		marginTop: "16px",
	},
	mt2: {
		marginTop: "32px",
	},
	textHighlight: {
		color: pdfColors.textHighlight,
	},
	textLight: {
		color: pdfColors.textLight,
	},
	textDanger: {
		color: defaultColors.colorDanger,
	},
	orderTotalFinalContainer: {
		padding: "5px 16px ",
		border: `1px solid ${defaultColors.colorDanger}`,
		marginLeft: "auto",
	},
	pageBorderTop: {
		width: "100vw",
		height: "32px",
		backgroundColor: defaultColors.colorActivationDark,
		marginBottom: "32px",
	},
	pageBorderBottom: {
		width: "100vw",
		height: "16px",
		backgroundColor: defaultColors.colorAwardGold,
		marginTop: "64px",
	},
	paymentBottom: {
		padding: "0 47px",
		marginLeft: "auto",
		marginTop: "auto",
	},
	wrap: {
		flexWrap: "wrap",
	},
	textCenter: {
		textAlign: "center",
	},
	alignLeft: {
		textAlign: "left",
	},
	divider: {
		width: "100%",
		height: "2px",
		backgroundColor: lightThemeColors.colorBorder,
		marginBottom: "28px",
		marginTop: "28px",
	},
	spacer: {
		width: "100%",
		marginBottom: "28px",
	},
	lineItem: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		width: "100%",
		border: `1px solid ${lightThemeColors.colorCopyDarkDark}`,
		borderTop: "0",
	},
	tableHeader: {
		borderTop: `1px solid ${lightThemeColors.colorCopyDarkDark}`,
	},
	pricingRow: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		width: "25.25%",
		textAlign: "center",
		marginLeft: "auto",
		borderLeft: `1px solid ${lightThemeColors.colorCopyDarkDark}`,
		borderRight: `1px solid ${lightThemeColors.colorCopyDarkDark}`,
		borderBottom: `1px solid ${lightThemeColors.colorCopyDarkDark}`,
	},
	cell: {
		textAlign: "center",
		borderRight: `1px solid ${lightThemeColors.colorCopyDarkDark}`,
		padding: "4pt",
		height: "100%",
	},
	entryCoverImg: {
		borderLeft: `1px solid ${lightThemeColors.colorCopyDarkDark}`,
		width: "34pt",
		height: "19pt",
		objectFit: "cover",
	},
	imgCell: {
		width: "10%",
		display: "flex",
		flexDirection: "row",
		justifyContent: "center",
		alignItems: "center",
	},
	idCell: {
		width: "10%",
	},
	hierarchyCell: {
		width: "20%",
		textAlign: "left",
	},
	verticalCell: {
		width: "20%",
		textAlign: "left",
	},
	titleCell: {
		width: "15%",
		textAlign: "left",
	},
	brandCell: {
		width: "12.5%",
	},
	priceCell: {
		width: "12.5%",
		borderRight: "0",
	},
	totalCell: {
		width: "50%",
		fontSize: "11pt",
	},
	totalPriceCell: {
		width: "50%",
		fontSize: "11pt",
		padding: "5pt 0",
	},
	bankTransferRow: {
		borderRight: `1px solid ${lightThemeColors.colorCopyDarkDark}`,
		borderLeft: `1px solid ${lightThemeColors.colorCopyDarkDark}`,
		padding: "5pt 8pt",
	},
	bankTransfer: {
		marginLeft: "auto",
	},
});

export function shortString(title: string, subStrAmt: number) {
	if (title.length > subStrAmt) {
		return title.slice(0, subStrAmt).trimEnd() + "...   ";
	} else {
		return title;
	}
}

const InvoicePdf = (props: {
	order: OrderModel;
	labels: Record<string, string>;
	countryIso?: string;
	companyTaxId?: string;
	showInvoiceNotes?: boolean;
}) => {
	const { order, labels, countryIso } = props;

	function returnInvoiceNotes(notes: string) {
		return (
			<View
				style={{
					...styles.editInvoiceContainer,
					...styles.mt2,
				}}
				wrap={false}
			>
				<Text style={styles.textDanger}>Invoice Notes: </Text>
				<Text>{notes}</Text>
			</View>
		);
	}

	return (
		<Document
			// title={String(order.id)}
			author={assetsConfig.companyNameShort}
			// subject={`Invoice #${order.id}`}
			keywords={assetsConfig.companyNameShort + ", Invoice Entry"}
		>
			<Page size="A4" style={styles.page} wrap>
				<View style={{ ...styles.twoCol }}>
					<Image style={styles.logo} src={config.assets.logos.localPng} />
				</View>

				<View style={styles.divider} />

				<View style={{ ...styles.twoCol }}>
					<View style={{ ...styles.flexCol }}>
						<View style={{ ...styles.flexRow }}>
							<Text style={{ ...styles.semiBold }}>Order Date: </Text>
							<Text>
								{order.createdDate &&
									getLocalTime(order.createdDate, false, "long")}
							</Text>
						</View>
						<View style={{ ...styles.flexRow }}>
							<Text style={{ ...styles.semiBold }}>Proforma Invoice No. </Text>
							<Text># {order.id}</Text>
						</View>
						<View style={{ ...styles.flexRow }}>
							<Text style={{ ...styles.semiBold }}>Payment Method: </Text>
							<Text>
								{TransactionTypeOptions[order.transactionType].label} Payment
							</Text>
						</View>
						<View style={{ ...styles.flexRow }}>
							<Text style={{ ...styles.semiBold }}>Payment Status: </Text>
							<Text>
								{order.transactionType === TransactionType.Offline
									? "To be PAID"
									: PaymentStatusOptions[order.statusId].label}
							</Text>
						</View>
						{/* only show P.O. for check/bank payments */}
						{(order.transactionType === TransactionType.Check ||
							order.transactionType === TransactionType.Bank) &&
							order.purchaseOrder && (
								<View style={{ ...styles.flexRow }}>
									<Text style={{ ...styles.semiBold }}>
										{labels.purchaseOrderLabel}:{" "}
									</Text>
									<Text>{order.purchaseOrder}</Text>
								</View>
							)}

						<View style={{ ...styles.flexCol, ...styles.mt1 }}>
							<Text style={{ ...styles.semiBold }}>Billed To:</Text>
							<Text>{shortString(order.companyName, 75)}</Text>
							<Text>{order.fullName}</Text>
							<Text>{order.companyAddress1}</Text>
							<Text>{order.companyAddress2}</Text>
							<Text>
								{order.companyCity}, {order.companyState}{" "}
								{order.companyPostalCode}
							</Text>
							<Text>{order.companyCountry}</Text>
							{props.companyTaxId && <Text>Tax ID: {props.companyTaxId}</Text>}
						</View>
					</View>

					<View style={{ ...styles.flexCol }}>
						<Text style={styles.semiBold}>Billed From:</Text>
						<Text style={styles.semiBold}>{labels.invoiceBusinessName}</Text>
						{labels.invoiceBilledFrom1 && (
							<Text>{labels.invoiceBilledFrom1}</Text>
						)}
						{labels.invoiceBilledFrom2 && (
							<Text>{labels.invoiceBilledFrom2}</Text>
						)}
						{labels.invoiceBilledFrom3 && (
							<Text>{labels.invoiceBilledFrom3}</Text>
						)}
						{labels.invoiceBilledFrom4 && (
							<Text>{labels.invoiceBilledFrom4}</Text>
						)}
						{labels.invoiceBilledFrom5 && (
							<Text>{labels.invoiceBilledFrom5}</Text>
						)}
						{labels.invoiceBilledFrom6 && (
							<Text>{labels.invoiceBilledFrom6}</Text>
						)}
						{labels.invoiceBilledFrom7 && (
							<Text>{labels.invoiceBilledFrom7}</Text>
						)}

						{labels.invoiceBilledFrom8 && (
							<Text>{labels.invoiceBilledFrom8}</Text>
						)}
						{labels.invoiceBilledFrom9 && (
							<Text>{labels.invoiceBilledFrom9}</Text>
						)}
						{labels.invoiceBilledFrom10 && (
							<Text>{labels.invoiceBilledFrom10}</Text>
						)}
						{labels.invoiceBilledFrom11 && (
							<Text>{labels.invoiceBilledFrom11}</Text>
						)}
						{labels.invoiceBilledFrom12 && (
							<Text>{labels.invoiceBilledFrom12}</Text>
						)}
					</View>
				</View>

				<View style={styles.spacer} />

				{/* table headers */}
				<View
					style={{
						...styles.lineItem,
						...styles.semiBold,
						...styles.tableHeader,
					}}
				>
					<View style={{ ...styles.cell, ...styles.imgCell }}></View>
					<View style={{ ...styles.cell, ...styles.idCell }}>
						<Text>{labels.invoiceColumnEntryId}</Text>
					</View>
					<View
						style={{
							...styles.cell,
							...styles.hierarchyCell,
						}}
					>
						<Text>{labels.invoiceColumnCategory}</Text>
					</View>
					<View style={{ ...styles.cell, ...styles.verticalCell }}>
						<Text>{labels.invoiceColumnVertical}</Text>
					</View>
					<View style={{ ...styles.cell, ...styles.titleCell }}>
						<Text>Title</Text>
					</View>
					<View style={{ ...styles.cell, ...styles.brandCell }}>
						<Text>{labels.invoiceColumnBrand}</Text>
					</View>
					<View style={{ ...styles.cell, ...styles.priceCell }}>
						<Text>Price</Text>
					</View>
				</View>

				{/* line items */}
				{order.lineItems.map((lineItem) => (
					<View
						key={lineItem.id}
						style={{
							...styles.lineItem,
						}}
						wrap={false}
					>
						<View style={{ ...styles.cell, ...styles.imgCell }}>
							<Image
								src={
									(lineItem.entry.coverImage &&
										getNonWebpThumbnailSrc(lineItem.entry.coverImage!)) ||
									Placeholder
								}
								style={styles.entryCoverImg}
							/>
						</View>
						<View style={{ ...styles.cell, ...styles.idCell }}>
							<Text>#{lineItem.entry.id}</Text>
						</View>
						<View style={{ ...styles.cell, ...styles.hierarchyCell }}>
							{getHierarchy(
								lineItem.entry.executions!,
								lineItem.entry.ancestry!,
								lineItem.entry.isCampaign!,
							)
								.slice(1) // Remove the first item
								.map((hierarchy, index) => (
									<Text key={index}>{hierarchy}</Text>
								))}
						</View>
						<View style={{ ...styles.cell, ...styles.verticalCell }}>
							<Text>{lineItem.entry.ancestry?.[0]?.name}</Text>
						</View>
						<View style={{ ...styles.cell, ...styles.titleCell }}>
							<Text>
								{lineItem.entry.title && shortString(lineItem.entry.title, 35)}
							</Text>
						</View>
						<View style={{ ...styles.cell, ...styles.brandCell }}>
							<Text>
								{lineItem.entry.brand && shortString(lineItem.entry.brand, 35)}
							</Text>
						</View>
						<View style={{ ...styles.cell, ...styles.priceCell }}>
							{/* <Text>{commafy(lineItem.totalLineAmount!, countryIso)}</Text> */}

							<Text>
								<Text style={{ ...styles.currencyText }}>
									{commafySymbol(countryIso)}
								</Text>
								{lineItem.totalLineAmount!}
							</Text>
						</View>
					</View>
				))}

				{/* {order.transactionType === TransactionType.Bank && (
					<View
						style={{
							...styles.lineItem,
							...styles.semiBold,
							...styles.bankTransferRow,
						}}
						wrap={false}
					>
						<Text
							style={{
								...styles.bankTransfer,
						
							}}
						>
							+ $25.00 Bank Transfer Fee
						</Text>
					</View>
				)} */}

				{order.pricingBreakdown &&
					order.pricingBreakdown.length > 0 &&
					order.pricingBreakdown.map((breakdown) => (
						<View
							style={{
								...styles.pricingRow,
								...styles.semiBold,
							}}
							wrap={false}
						>
							<View
								style={{
									...styles.cell,
									...styles.brandCell,
									...styles.mlAuto,
									...styles.totalCell,
								}}
							>
								<Text>{breakdown.label}</Text>
							</View>
							<View
								style={{
									...styles.cell,
									...styles.priceCell,
									...styles.totalPriceCell,
								}}
							>
								<Text>
									<Text style={{ ...styles.currencyText, ...styles.semiBold }}>
										{commafySymbol(countryIso)}
									</Text>
									{breakdown.amount.toFixed(2)}
								</Text>
							</View>
						</View>
					))}

				{/* total row */}
				<View
					style={{
						...styles.pricingRow,
						...styles.semiBold,
					}}
					wrap={false}
				>
					<View
						style={{
							...styles.cell,
							...styles.brandCell,
							...styles.mlAuto,
							...styles.totalCell,
						}}
					>
						<Text>Total</Text>
					</View>
					<View
						style={{
							...styles.cell,
							...styles.priceCell,
							...styles.totalPriceCell,
						}}
					>
						<Text>
							<Text style={{ ...styles.currencyText, ...styles.semiBold }}>
								{commafySymbol(countryIso)}
							</Text>
							{order.orderTotal.toFixed(2)}
						</Text>
					</View>
				</View>

				{props.showInvoiceNotes &&
					order.notes &&
					returnInvoiceNotes(order.notes)}

				<View style={{ ...styles.spacer }} />

				{(order.transactionType === TransactionType.Offline ||
					order.transactionType === TransactionType.Bank) && (
					<View style={{ ...styles.flexCol }}>
						{labels.invoiceInstructions1 && (
							<Text wrap={false}>{labels.invoiceInstructions1}</Text>
						)}
						{labels.invoiceInstructions2 && (
							<Text wrap={false}>{labels.invoiceInstructions2}</Text>
						)}
						{labels.invoiceInstructions3 && (
							<Text wrap={false}>{labels.invoiceInstructions3}</Text>
						)}
						{labels.invoiceInstructions4 && (
							<Text wrap={false}>{labels.invoiceInstructions4}</Text>
						)}
						{labels.invoiceInstructions5 && (
							<Text wrap={false}>{labels.invoiceInstructions5}</Text>
						)}
						{labels.invoiceInstructions6 && (
							<Text wrap={false}>{labels.invoiceInstructions6}</Text>
						)}
						{labels.invoiceInstructions7 && (
							<Text wrap={false}>{labels.invoiceInstructions7}</Text>
						)}
						{labels.invoiceInstructions8 && (
							<Text wrap={false}>{labels.invoiceInstructions8}</Text>
						)}
						{labels.invoiceInstructions9 && (
							<Text wrap={false}>{labels.invoiceInstructions9}</Text>
						)}
						{labels.invoiceInstructions10 && (
							<Text wrap={false}>{labels.invoiceInstructions10}</Text>
						)}
						{labels.invoiceInstructions11 && (
							<Text wrap={false}>{labels.invoiceInstructions11}</Text>
						)}
						{labels.invoiceInstructions12 && (
							<Text wrap={false}>{labels.invoiceInstructions12}</Text>
						)}
					</View>
				)}

				<View style={styles.footer} fixed>
					<View
						style={{
							...styles.flexCol,
							...styles.textCenter,
							...styles.mtAuto,
						}}
					>
						<Text
							style={{
								...styles.semiBold,
								...styles.mbHalf,
							}}
						>
							{labels.invoiceFooter1}
						</Text>
						<Text>{labels.invoiceFooter2}</Text>
					</View>
					<View style={styles.divider} />
					<View
						style={{
							...styles.flexCol,
							...styles.textCenter,
						}}
					>
						{/* <Text
							style={{
								...styles.mbHalf,
							}}
						>
							{labels.invoiceAddress1} {labels.invoiceAddress2}
						</Text>
						<Text
							style={{
								...styles.mb1,
							}}
						>
							{labels.invoiceCity}, {labels.invoiceState} {labels.invoiceZip}{" "}
							{labels.invoiceCountry}
						</Text> */}
						<Text>{labels.invoiceWebsite}</Text>
					</View>
				</View>
			</Page>
		</Document>
	);
};

export default InvoicePdf;
